import React from "react";
import { useNavigate } from "react-router-dom";
import { WebPaths } from "../../../../routes/data";
import Button from "../../components/ui/Button";
import {
  FaBrain,
  FaGlobe,
  FaArrowTrendUp,
  FaWandMagicSparkles,
  FaRobot,
  FaLightbulb,
  FaChartBar,
  FaCircleCheck,
  FaListCheck,
  FaMedal,
} from "react-icons/fa6";
import { US, CA, GB } from "country-flag-icons/react/3x2";
import { motion } from "framer-motion";

const features = [
  {
    name: "Intelligence-Powered",
    description:
      "Cutting-edge AI technology that prepares you for success with personalized interview simulations.",
    icon: <FaBrain className="text-xl" />,
    theme: {
      text: "text-blue-600",
      bg: "bg-blue-50",
      hoverBg: "group-hover:bg-blue-600",
      hoverText: "group-hover:text-white",
      border: "hover:border-blue-200",
      shadow: "hover:shadow-blue-500/10",
      visualAccent: "bg-blue-600",
    },
    visual: (
      <div className="relative mt-4 min-h-[160px] overflow-hidden rounded-xl bg-blue-50/30 p-4 border border-blue-100/50 group-hover:border-blue-200 transition-colors duration-500">
        <div className="flex flex-col gap-2">
          <div className="h-2 w-24 rounded bg-blue-200/50" />
          <div className="flex items-center gap-2">
            <div className="h-7 w-32 rounded-lg bg-blue-600 shadow-[0_4px_12px_rgba(37,99,235,0.15)] transition-transform duration-500 group-hover:scale-[1.03]" />
            <div className="h-7 w-12 rounded-lg bg-white border border-blue-100" />
          </div>
          <div className="h-2 w-16 rounded bg-blue-100/50" />
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          {[FaWandMagicSparkles, FaRobot, FaLightbulb, FaBrain].map(
            (Icon, i) => (
              <motion.div
                key={i}
                animate={{ y: [0, -4, 0] }}
                transition={{
                  duration: 3,
                  delay: i * 0.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className={`h-11 rounded-lg border border-blue-100 bg-white flex items-center justify-center text-blue-400 transition-all duration-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 ${
                  i === 2 ? "shadow-sm ring-1 ring-blue-100" : ""
                }`}
              >
                <Icon className="text-lg" />
              </motion.div>
            ),
          )}
        </div>

        <motion.div
          animate={{ x: [80, 120, 100], y: [30, 50, 40] }}
          whileHover={{ scale: 1.2 }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute text-xl pointer-events-none drop-shadow-sm z-10 grayscale-0"
        >
          ✨
        </motion.div>
      </div>
    ),
  },
  {
    name: "Global Expertise",
    description:
      "Comprehensive support for educational opportunities across Australia, Canada, Ireland, UK, and USA.",
    icon: <FaGlobe className="text-xl" />,
    theme: {
      text: "text-blue-02",
      bg: "bg-blue-ea",
      hoverBg: "group-hover:bg-blue-02",
      hoverText: "group-hover:text-white",
      border: "hover:border-blue-02/30",
      shadow: "hover:shadow-blue-02/10",
      visualAccent: "bg-blue-02",
    },
    visual: (
      <div className="relative mt-4 min-h-[160px] overflow-hidden rounded-xl bg-blue-ea/50 p-4 border border-blue-ea/10 group-hover:border-blue-ea/30 transition-colors duration-500">
        <div className="space-y-3">
          {[
            { label: "USA", icon: <US className="w-6 h-6" />, delay: 0 },
            { label: "Canada", icon: <CA className="w-6 h-6" />, delay: 0.5 },
            {
              label: "UK & More",
              icon: <GB className="w-6 h-6" />,
              delay: 1.0,
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ x: 0 }}
              animate={{ x: [0, 5, 0] }}
              transition={{
                duration: 4,
                delay: item.delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{ x: 10, scale: 1.02 }}
              className={`flex items-center gap-3 p-2.5 rounded-xl bg-white border border-blue-02/10 shadow-sm transition-shadow duration-500 group-hover:shadow-md ${
                i === 1 ? "ml-6" : ""
              }`}
            >
              <div className="h-9 w-9 rounded-full bg-blue-ea flex items-center justify-center border border-blue-02/10 text-lg shadow-inner">
                {item.icon}
              </div>
              <div className="flex-1 space-y-1.5 font-spartan">
                <div className="h-2 w-20 rounded bg-blue-02/10" />
                <div className="flex gap-1">
                  <div className="h-1.5 w-8 rounded bg-blue-02/30" />
                  <div className="h-1.5 w-4 rounded bg-blue-02/10" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    ),
  },
  {
    name: "Proven Results",
    description:
      "Higher approval rates through smart application review and expert guidance every step of the way.",
    icon: <FaArrowTrendUp className="text-xl" />,
    theme: {
      text: "text-purple-a8",
      bg: "bg-lavender-ea",
      hoverBg: "group-hover:bg-purple-a8",
      hoverText: "group-hover:text-white",
      border: "hover:border-purple-a8",
      shadow: "hover:shadow-purple-a8/10",
      visualAccent: "bg-purple-a8",
    },
    visual: (
      <div className="relative mt-4 min-h-[160px] overflow-hidden rounded-xl bg-purple-a8/30 p-4 border border-purple-a8/20 group-hover:border-purple-a8/50 transition-colors duration-500">
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: FaArrowTrendUp, label: "Up" },
            { icon: FaChartBar, label: "Chart" },
            { icon: FaMedal, label: "Medal" },
            { icon: FaCircleCheck, label: "Success" },
          ].map((item, i) => (
            <motion.div
              key={i}
              animate={{ scale: [1, 1.03, 1] }}
              transition={{
                duration: 3,
                delay: i * 0.4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{
                scale: 1.05,
                borderColor: "#A881DB",
              }}
              className="p-3 rounded-xl bg-white border border-purple-a8/20 shadow-sm transition-all duration-500 flex flex-col items-center gap-2 group/item"
            >
              <item.icon className="text-lg text-purple-a8/60 group-hover/item:text-purple-a8 group-hover:scale-110 transition-all duration-300" />
              <div className="h-1 w-8 rounded bg-lavender-ea" />
            </motion.div>
          ))}
        </div>
      </div>
    ),
  },
];

const ValuePropositionVariant: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-linear-to-b from-blue-00/20 to-white py-20 lg:py-24 mt-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Fixed Visibility Header */}
        <div className="mx-auto max-w-4xl text-center mb-16">
          <h2 className="text-black font-spartan text-4xl font-light tracking-tight sm:text-5xl md:text-5xl flex flex-col items-center">
            <span className="block mb-4 text-black-1a">
              Why More People Are
            </span>
            <span className="text-blue-00 italic">Choosing SeamlessVisa</span>
          </h2>
          <p className="mt-8 text-lg leading-8 text-dark-6b max-w-2xl mx-auto opacity-90">
            Experience a streamlined visa application process with our
            intelligent platform, global expertise, and a proven track record of
            successful outcomes across multiple destinations.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: "easeOut",
              }}
              key={feature.name}
              className={`group relative flex flex-col overflow-hidden rounded-3xl border border-grey-eb bg-white p-8 transition-all duration-500 ${feature.theme.border} ${feature.theme.shadow} shadow-sm`}
            >
              <div className="mb-8 flex items-center gap-4">
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-full transition-all duration-300 ${feature.theme.bg} ${feature.theme.text} ${feature.theme.hoverBg} ${feature.theme.hoverText} border border-transparent`}
                >
                  {feature.icon}
                </div>
              </div>

              <h3 className="text-black-1a font-spartan text-2xl leading-tight">
                {feature.name}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-dark-6b opacity-80 group-hover:opacity-100 transition-opacity duration-500">
                {feature.description}
              </p>

              {/* Enhanced Visual Component */}
              <div className="mt-6 grow flex flex-col justify-end">
                {feature.visual}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 flex justify-center">
          <Button
            label="Get Started"
            onClick={() => navigate(WebPaths.Join)}
            variant="primary"
            size="large"
            startIcon={<FaListCheck className="h-5 w-5" />}
            iconHoverStyle="scale"
          />
        </div>
      </div>
    </section>
  );
};

export default ValuePropositionVariant;
